// Single source for both the shortcut handler and every place a shortcut is displayed.

export const GO_SEQUENCE_TIMEOUT_MS = 1200;

export const DOUBLE_ESCAPE_TIMEOUT_MS = 500;

/** Escape mashed to close overlays can't chain into an accidental Esc-Esc sign-out. */
export const OVERLAY_DISMISS_COOLDOWN_MS = 300;

export const CORRECTION_KEY = "c";

export const SWITCH_USER_TOKENS: string[] = ["Esc", "Esc"];

const isMacPlatform = (): boolean =>
  typeof navigator !== "undefined" &&
  /mac/i.test(navigator.userAgent ?? navigator.platform ?? "");

export type ShortcutSurface = "workstation" | "frontDesk";

export type GoToTarget =
  | "desk"
  | "schedule"
  | "board"
  | "calls"
  | "email"
  | "todos"
  | "settings";

interface GoToShortcut {
  /** Always lowercase. */
  key: string;
  target: GoToTarget;
  label: string;
  tokens: string[];
  adminOnly?: boolean;
}

export const GO_TO_SHORTCUTS: GoToShortcut[] = [
  { key: "d", target: "desk", label: "Front desk", tokens: ["G", "D"] },
  { key: "s", target: "schedule", label: "Schedule", tokens: ["G", "S"] },
  { key: "b", target: "board", label: "Clinic board", tokens: ["G", "B"] },
  { key: "c", target: "calls", label: "Calls", tokens: ["G", "C"] },
  { key: "e", target: "email", label: "Email", tokens: ["G", "E"] },
  { key: "t", target: "todos", label: "To-dos", tokens: ["G", "T"] },
  {
    key: ",",
    target: "settings",
    label: "Clinic settings",
    adminOnly: true,
    tokens: ["G", ","],
  },
];

/** The front desk's go-tos stay on its own pages. */
export const GO_TO_TARGETS = {
  workstation: ["schedule", "board", "calls", "email", "todos", "settings"],
  frontDesk: ["desk", "schedule", "calls", "email"],
} as const satisfies Record<ShortcutSurface, readonly GoToTarget[]>;

const searchShortcutTokens = (): string[] =>
  isMacPlatform() ? ["⌘", "K"] : ["Ctrl", "K"];

export const goToTargetForKey = (key: string): GoToTarget | undefined =>
  GO_TO_SHORTCUTS.find((s) => s.key === key.toLowerCase())?.target;

export interface ShortcutRow {
  tokens: string[];
  label: string;
}

export const generalShortcutRows = (): ShortcutRow[] => [
  { tokens: searchShortcutTokens(), label: "Search" },
  { tokens: ["C"], label: "Correct Brian" },
  { tokens: ["?"], label: "Keyboard shortcuts" },
  { tokens: ["Esc"], label: "Close menus and dialogs" },
  { tokens: SWITCH_USER_TOKENS, label: "Switch user" },
];

export const goToShortcutRows = (
  surface: ShortcutSurface,
  hasAdminAccess: boolean,
): ShortcutRow[] => {
  const targets: readonly GoToTarget[] = GO_TO_TARGETS[surface];
  return GO_TO_SHORTCUTS.filter(
    (s) => targets.includes(s.target) && (!s.adminOnly || hasAdminAccess),
  ).map(({ tokens, label }) => ({ tokens, label }));
};
